"use client";

import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useState, useMemo } from 'react'; // FIXED: Add useMemo for safe browser API access
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/utils/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react'; // Only lucide-react
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Building2, User, Users, Building, Star, CreditCard, Shield, Zap, Clock, Users2, Mail, FileText as FileTextIcon, Clock as ClockIcon, Briefcase, HelpCircle } from 'lucide-react'; // Only lucide-react - no Heroicons
import { Checkbox } from '@/components/ui/checkbox';
import { ColorPicker } from '@/components/ColorPicker';
import { showError, showSuccess } from '@/utils/toast';
import * as z from 'zod'; // FIXED: Add missing zod import
import { useForm } from 'react-hook-form'; // FIXED: Add missing react-hook-form import
import { zodResolver } from '@hookform/resolvers/zod'; // FIXED: Add missing zodResolver import

const schema = z.object({
  companyName: z.string().min(1, 'Company name is required'),
  subdomain: z.string().min(1, 'Subdomain is required').regex(/^[a-z0-9-]+$/i, 'Invalid subdomain (letters, numbers, hyphens only)'),
  primaryColor: z.string().default('#3b82f6'),
  secondaryColor: z.string().default('#6b7280'),
});

type FormData = z.infer<typeof schema>;

const SignUp = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userRole, setUserRole] = useState<'admin' | 'client'>('admin'); // Default to admin (business sign-up)
  const [selectedTier, setSelectedTier] = useState<'basic' | 'pro' | 'enterprise'>('basic'); // New: Tier selection
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month'); // New: Billing interval toggle
  const [termsAgreed, setTermsAgreed] = useState<boolean>(false); // New: Terms checkbox

  useEffect(() => {
    if (!isLoading && user) {
      navigate('/onboarding');
    }
  }, [user, isLoading, navigate]);

  const handleRoleSubmit = (role: 'admin' | 'client') => {
    setUserRole(role);
    // Proceed with Auth UI for the selected role
  };

  // Tier plans with benefits (same as Pricing page)
  const tiers = [
    {
      id: 'basic' as const,
      name: 'Basic',
      icon: User,
      monthlyPrice: '£5 / month',
      yearlyPrice: '£54 / year (Save 10%)',
      description: 'Perfect for getting started',
      features: [
        'Up to 5 projects',
        'Up to 2 agents (team members)',
        'Up to 5 clients',
        'Basic time tracking',
        'Email support'
      ],
      popular: false,
    },
    {
      id: 'pro' as const,
      name: 'Pro',
      icon: Users,
      monthlyPrice: '£79 / month',
      yearlyPrice: '£853 / year (Save 10%)',
      description: 'Best for growing teams',
      features: [
        'Up to 20 projects',
        'Up to 50 agents (team members)',
        'Up to 500 clients',
        'Advanced analytics & reports',
        'Client portals with branding',
        'Priority email support',
        'Custom themes & integrations'
      ],
      popular: true,
    },
    {
      id: 'enterprise' as const,
      name: 'Enterprise',
      icon: Building,
      monthlyPrice: '£399 / month',
      yearlyPrice: '£4,309 / year (Save 10%)',
      description: 'Unlimited scale for your business',
      features: [
        'Unlimited projects, agents, & clients',
        'Everything in Pro',
        'Dedicated account manager',
        'Custom integrations (API/Slack)',
        '99.9% uptime SLA',
        'Advanced security & compliance'
      ],
      popular: false,
    },
  ];

  const handleTierSelect = (tier: 'basic' | 'pro' | 'enterprise') => {
    setSelectedTier(tier);
  };

  // FIXED: Compute redirect URL safely with useMemo - only access window on client-side
  const safeOrigin = useMemo(() => {
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return 'http://localhost:8080'; // Fallback for SSR (won't be used in CSR)
  }, []);

  const redirectTo = useMemo(() => {
    return safeOrigin + (searchParams.get('redirect') ? `?redirect=${encodeURIComponent(searchParams.get('redirect') || '')}` : '');
  }, [safeOrigin, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative overflow-hidden">
      {/* Subtle Lock Patterns */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg width=%2240%22 height=%2240%22 viewBox=%220 0 40 40%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cpath d=%22M20 10 V15 M20 15 H25 M20 15 H15 M20 25 V30 M20 30 H25 M20 30 H15%22 stroke=%22%233b82f6%22 stroke-width=%221%22 fill=%22none%22 opacity=%220.1%22/%3E%3C/svg%3E')]"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <Card className="w-full max-w-2xl relative z-10">
        <CardHeader>
          <CardTitle>Join WightWay Connect</CardTitle>
          <CardDescription>Create your account to get started</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Hero Section */}
          <div className="text-center mb-8 p-6 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg">
            <h2 className="text-2xl font-bold mb-2">Empower Your Business with WightWay Connect</h2>
            <p className="text-muted-foreground mb-4">Manage projects, track time, invoice clients, and collaborate—all in one secure platform.</p>
            <div className="flex justify-center space-x-4 text-sm text-muted-foreground">
              <span>• Visual task boards</span>
              <span>• Automated billing</span>
              <span>• Branded client portals</span>
            </div>
          </div>

          {/* Role Selector with Icons */}
          <div className="space-y-4 mb-4">
            <Label className="text-sm font-medium">Account Type</Label>
            <div className="grid grid-cols-1 gap-3">
              <Button
                variant={userRole === 'admin' ? 'default' : 'outline'}
                onClick={() => handleRoleSubmit('admin')}
                className="h-auto p-4 flex flex-col items-start justify-center min-h-[100px] text-left"
              >
                <Building2 className="h-8 w-8 mb-2" />
                <div className="font-semibold mb-1">Business Account (Admin)</div>
                <div className="text-xs text-muted-foreground">Create/manage company, invite team/clients</div>
              </Button>
              <Button
                variant={userRole === 'client' ? 'default' : 'outline'}
                onClick={() => handleRoleSubmit('client')}
                className="h-auto p-4 flex flex-col items-start justify-center min-h-[100px] text-left"
              >
                <User className="h-8 w-8 mb-2" />
                <div className="font-semibold mb-1">Client Account</div>
                <div className="text-xs text-muted-foreground">Join a company, view/sign documents</div>
              </Button>
            </div>
            {userRole === 'client' && (
              <Alert>
                <AlertDescription>
                  Client accounts are invite-only. If you have an invite code from your business admin, enter it during setup. Otherwise, <Link to="/contact" className="text-primary hover:underline">contact your admin</Link> to get invited.
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Tier Picker for Business Accounts with Icons */}
          {userRole === 'admin' && (
            <div className="space-y-6 mb-6">
              <Label className="text-sm font-medium">Select Your Plan</Label>
              <div className="flex justify-center items-center space-x-4 mb-4">
                <span className="text-sm font-medium text-muted-foreground">Billing Cycle:</span>
                <ToggleGroup type="single" value={billingInterval} onValueChange={(value: string) => setBillingInterval(value as 'month' | 'year')} className="bg-background border rounded-lg p-1">
                  <ToggleGroupItem value="month">Monthly</ToggleGroupItem>
                  <ToggleGroupItem value="year">Yearly (Save 10%)</ToggleGroupItem>
                </ToggleGroup>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {tiers.map((tier) => (
                  <Card
                    key={tier.id}
                    className={`cursor-pointer transition-all duration-300 hover:shadow-lg border-2 rounded-lg ${
                      selectedTier === tier.id
                        ? 'border-primary ring-2 ring-primary/20 shadow-lg' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => handleTierSelect(tier.id)}
                  >
                    <CardHeader className="text-center p-4">
                      <tier.icon className={`h-8 w-8 mx-auto mb-2 ${selectedTier === tier.id ? 'text-primary' : 'text-muted-foreground'}`} />
                      <CardTitle className={`text-lg font-bold ${selectedTier === tier.id ? 'text-primary' : 'text-muted-foreground'}`}>
                        {tier.name}
                      </CardTitle>
                      <div className={`text-2xl font-bold ${selectedTier === tier.id ? 'text-primary' : 'text-muted-foreground'}`}>
                        {billingInterval === 'year' ? tier.yearlyPrice : tier.monthlyPrice}
                      </div>
                      {tier.popular && <Badge variant="secondary" className="mt-2">Most Popular</Badge>}
                      <CardDescription>{tier.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2 p-4">
                      <ul className="space-y-1">
                        {tier.features.map((feature, i) => (
                          <li key={i} className="flex items-center space-x-2">
                            <Check className={`h-3 w-3 ${selectedTier === tier.id ? 'text-primary' : 'text-muted-foreground'}`} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <p className="text-xs text-muted-foreground text-center">
                7-day money back guarantee on all plans. Upgrade anytime from your dashboard.
              </p>
            </div>
          )}

          {/* Privacy & Terms Checkbox */}
          <div className="space-y-2 mt-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="terms"
                checked={termsAgreed}
                onCheckedChange={(checked) => setTermsAgreed(!!checked)}
                required
              />
              <Label htmlFor="terms" className="text-xs">
                I agree to the <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
              </Label>
            </div>
          </div>

          <Auth
            supabaseClient={supabase}
            providers={['google', 'github']}
            appearance={{
              theme: ThemeSupa,
              variables: {
                default: {
                  colors: {
                    defaultButtonBackground: 'var(--primary)',
                    defaultButtonText: 'var(--primary-foreground)',
                  },
                },
              },
            }}
            theme="light"
            redirectTo={redirectTo}
            socialLayout="horizontal"
            view="sign_up"
          />
          <div className="mt-4 text-center text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignUp;